// src/pages/TranscriptomeInfo/index.js
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import PlotContainer from "../../Components/Common/PlotContainer";

// Page-specific Components
import PlotTypeSelection from "./components/PlotTypeSelection";
import PlotOptions from "./components/PlotOptions";

// API
import {
  submitTranscriptomeInfoJob,
  fetchTranscriptomeInfoData,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useTranscriptomeInfoSettings } from "./hooks/useTranscriptomeInfoSettings";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useTranscriptomeInfoShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import ActionButton from "../../Components/Common/ActionButton";

const TranscriptomeInfo = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // ✅ Add initial loading
  const [plotData, setPlotData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [fastaData, setFastaData] = useState(null);
  const [error, setError] = useState(null);
  const [defaultTranscript, setDefaultTranscript] = useState(""); // ✅ Add default transcript state
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer, to allow for auto scrolling
  const plotContainerRef  = useRef(null);

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useTranscriptomeInfoSettings();

  // Use the TranscriptomeInfo specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useTranscriptomeInfoShortCode({
      location,
      updateMultipleSettings,
      defaultTranscript,
    });

  // Clear any generated output when plot type changes
  useEffect(() => {
    setPlotData(null);
    setTableData(null);
    setFastaData(null);
    setError(null);
  }, [settings.plotType]);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (loading) return;
    setAutoRunPlot(true);
  }, [loading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // ✅ NEW: Load default transcript data on component mount
  useEffect(() => {
    const loadDefaultData = async () => {
      try {
        setInitialLoading(true);
        const response = await fetchTranscriptomeInfoData(projectId);
        const data = response?.data || response || {};

        // Set default transcript if available
        if (data.default_tran) {
          setDefaultTranscript(data.default_tran);
          // Update settings with default transcript for relevant plot types
          updateMultipleSettings({
            customTranInput: data.default_tran,
            singleNucTran: data.default_tran,
          });
          console.log("Default transcript loaded:", data.default_tran);
        }
      } catch (error) {
        console.error("Failed to load default transcript data:", error);
        // Don't show error toast for this as it's not critical
      } finally {
        setInitialLoading(false);
      }
    };

    if (projectId && !location.state?.isFromShortCode) {
      loadDefaultData();
    } else {
      setInitialLoading(false);
    }
  }, [projectId, updateMultipleSettings, location.state?.isFromShortCode]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        loading,
        initialLoading,
        plotType: settings.plotType,
      });

      if (!loading && !initialLoading && settings.plotType) {
        console.log(
          "All conditions met, generating TranscriptomeInfo analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [autoRunPlot, loading, initialLoading, settings.plotType, setAutoRunPlot]);

  // ✅ Enhanced reset functionality to restore defaults
  const handleReset = useCallback(() => {
    resetSettings();

    // ✅ Restore default transcript for relevant fields
    if (defaultTranscript) {
      setTimeout(() => {
        updateMultipleSettings({
          customTranInput: defaultTranscript,
          singleNucTran: defaultTranscript,
        });
      }, 0);
    }

    setPlotData(null);
    setTableData(null);
    setFastaData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, defaultTranscript, updateMultipleSettings]);

  // Build payload for API
  const buildPayload = useCallback(() => {
    // Use the TranscriptomeInfo specific payload creator
    return createPlotSpecificPayload.transcriptomeInfo({
      settings,
      projectId,
      userPassedShortCode,
    });
  }, [settings, projectId, userPassedShortCode]);

  // Generate plot/table/fasta
  const handleGenerate = useCallback(async () => {
    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPlotData(null);
      setTableData(null);
      setFastaData(null);

      const payloadData = buildPayload();
      console.log("Sending transcriptome info payload:", payloadData);

      const response = await submitTranscriptomeInfoJob(payloadData);
      const data = response?.data || response || {};
      console.log(data);

      // Handle different response types
      if (data.message === "user logged out") {
        navigate("/login");
        return;
      } else if (data.message === "fasta") {
        setFastaData({
          filename: data.csv_href,
          message: "FASTA file generated successfully",
        });
        toast.success("FASTA file generated successfully");
      } else if (data.message === "orftable") {
        // setTableData(data);
        setTableData({
          filename: data.filename,
          result_rows: data.result_rows,
          total_rows: data.total_rows,
          csv_href: data.csv_href,
          header_row: data.header_row,
        });

        toast.success("Table generated successfully");
      } else if (
        data.message &&
        data.message !== "fasta" &&
        data.message !== "orftable"
      ) {
        setError(data.message);
        toast.error(data.message);
      } else if (data.plot) {
        setPlotData(data.plot);
        toast.success("Plot generated successfully");
      } else {
        setError("No valid response received");
        toast.error("No valid response received");
      }
    } catch (e) {
      console.error("Generation error:", e);
      const errorMsg = e.response?.data?.message || e.message || "Server error";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [validateSettings, buildPayload, navigate]);

  // Get button text based on plot type
  const getButtonText = () => {
    if (loading) return "Generating…";
    switch (settings.plotType) {
      case "nuc_comp_single":
      case "gene_count":
      case "codon_usage":
        return "View Plot";
      case "orfstats":
        return "Generate Table";
      case "fetch_seq":
        return "Generate Fasta";
      default:
        return "Generate";
    }
  };

  // ✅ Show loading spinner while fetching initial data
  if (initialLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Rest of your component remains the same...
  document.title = "Transcriptome Info | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Transcriptome Info" },
            ]}
          />

          {/* Page Header */}
          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Transcriptome Information
                </h4>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={loading}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Reset All
                </Button>
              </div>
            </Col>
          </Row>

          {/* Plot Type Selection */}
          <Row className="mb-1">
            <Col lg={12}>
              <PlotTypeSelection
                selectedType={settings.plotType}
                onTypeChange={(type) => updateSetting("plotType", type)}
              />
            </Col>
          </Row>

          {/* Plot Options */}
          {settings.plotType && (
            <Row className="mb-1">
              <Col lg={12}>
                <PlotOptions
                  plotType={settings.plotType}
                  settings={settings}
                  onUpdateSetting={updateSetting}
                  defaultTranscript={defaultTranscript} // ✅ Pass default transcript
                />
              </Col>
            </Row>
          )}

          {/* Generate Button */}
          {settings.plotType && (
            <Row className="mb-4">
              <Col lg={12}>
                <div className="text-end">
                  <ActionButton
                    onClick={handleGenerate}
                    loading={loading}
                    buttonType={
                      settings.plotType === "fetch_seq"
                        ? "fasta"
                        : settings.plotType == "orfstats"
                        ? "table"
                        : "plot"
                    }
                    customText={getButtonText()}
                  />{" "}
                </div>
              </Col>
            </Row>
          )}

          {/* Results Container (Plot/Table) */}
          {(plotData || tableData || loading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={
                    settings.plotType === "orfstats" ? tableData : plotData
                  }
                  loading={loading}
                  error={error}
                  title={
                    settings.plotType === "orfstats"
                      ? "ORF Results Table"
                      : "Transcriptome Info Plot"
                  }
                  isTable={settings.plotType === "orfstats"}
                />
              </Col>
            </Row>
          )}

          {/* FASTA Result */}
          {fastaData && (
            <Row className="mt-3">
              <Col lg={12}>
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">
                      <i className="ri-file-text-line me-2"></i>
                      FASTA File
                    </h5>
                  </div>
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "#e9f7ec",
                            color: "#2F9E37",
                            border: "1px solid #2F9E37",
                            fontWeight: 600,
                          }}
                        >
                          FASTA
                        </span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <a
                        href={fastaData.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-success btn-sm"
                        style={{
                          borderColor: "#2F9E37",
                          color: "#2F9E37",
                        }}
                      >
                        <i className="ri-download-line me-1"></i>
                        Download FASTA
                      </a>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TranscriptomeInfo;
